import Image from "next/image";
import Link from "next/link";
import { Button, Typography } from "@/components/ui";
import { env } from "@/lib/environment";
import { TrackingId } from "@/lib/tracking";

interface CTABox {
  title: string;
  buttonText: string;
  buttonUrl: string;
  imageUrl: string;
  imageAlt: string;
  imageWidth?: number;
  imageHeight?: number;
  buttonTrackingId?: TrackingId;
  buttonNoTrack?: boolean;
}

interface SplitCardCTAProps {
  ctaBoxes: { left: CTABox; right: CTABox };
}

export function SplitCardCTA({ ctaBoxes }: SplitCardCTAProps) {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <div
          className="flex flex-col gap-4 lg:flex-row bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-8 shadow-lg bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${env.NEXT_PUBLIC_CDN_URL}/images/shared/bg-2.5ZLBvjliwbrn.png)`,
          }}
        >
          {/* Left Section */}
          <div className="flex-1 flex flex-col gap-8 md:gap-0 md:flex-row items-center justify-between pr-8">
            <div className="flex-1 text-center md:text-left">
              <Typography
                variant="h2"
                as="h2"
                className="mb-6 text-xl text-text-primary leading-tight"
              >
                {ctaBoxes.left.title}
              </Typography>
              <Link
                href={ctaBoxes.left.buttonUrl}
                {...(ctaBoxes.left.buttonNoTrack ? { "data-mixpanel-disabled": true } : {})}
                {...(ctaBoxes.left.buttonTrackingId
                  ? { "data-mixpanel-id": ctaBoxes.left.buttonTrackingId }
                  : { "data-mixpanel-id": "cta-start-free-trial" })}
              >
                <Button variant="primary" size="lg" asChild>
                  {ctaBoxes.left.buttonText}
                </Button>
              </Link>
            </div>
            <div className="w-full md:w-auto ml-6 flex-shrink-0">
              <Image
                src={ctaBoxes.left.imageUrl}
                alt={ctaBoxes.left.imageAlt}
                title={ctaBoxes.left.imageAlt}
                width={142}
                height={150}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 142px"
                className="max-w-full mx-auto"
              />
            </div>
          </div>

          {/* Vertical Separator Line */}
          <div className="lg:w-px w-full h-[1px] lg:h-32 md:p-0 bg-gray-300 mx-4"></div>

          {/* Right Section */}
          <div className="flex-1 flex flex-col md:flex-row gap-8 md:gap-0 items-center justify-between lg:pl-8">
            <div className="flex-1 text-center md:text-left">
              <Typography
                variant="h2"
                as="h2"
                className="mb-6 text-xl text-text-primary leading-tight"
              >
                {ctaBoxes.right.title}
              </Typography>
              <Link
                href={ctaBoxes.right.buttonUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full"
                {...(ctaBoxes.right.buttonNoTrack ? { "data-mixpanel-disabled": true } : {})}
                {...(ctaBoxes.right.buttonTrackingId
                  ? { "data-mixpanel-id": ctaBoxes.right.buttonTrackingId }
                  : { "data-mixpanel-id": "cta-book-a-demo" })}
              >
                <Button variant="primary" size="lg" asChild>
                  {ctaBoxes.right.buttonText}
                </Button>
              </Link>
            </div>
            <div className="w-full md:w-auto ml-6 flex-shrink-0">
              <Image
                src={ctaBoxes.right.imageUrl}
                alt={ctaBoxes.right.imageAlt}
                width={142}
                height={150}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 142px"
                className="max-w-full mx-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
